/**
 * Portal push re-sync.
 *
 * Mounted in the portal layout. This hook never asks for permission: on iOS
 * (and Safari generally) `Notification.requestPermission` only works inside a
 * tap, so the ask lives on the toggle in Portal Profile. What this does is
 * keep an already-granted device registered:
 *
 *   - reuse the browser's existing subscription when its key matches ours;
 *   - re-subscribe only when the VAPID key changed;
 *   - tell the server whenever the endpoint differs from the one last saved,
 *     so a rotated endpoint or a fresh install is picked up.
 *
 * The saved endpoint is cleared on portal logout (clientAuth.clearClientAuth)
 * so a different athlete on the same device registers under their own account.
 */

import { useEffect } from 'react'
import { getClientSession } from '../api/clientAuth'
import {
  PUSH_ENDPOINT_KEY,
  VAPID_PUBLIC_KEY,
  applicationServerKey,
  saveSubscriptionToServer,
  subscriptionKeyMatches,
} from '../api/clientPortal/pushSubscriptions'

/**
 * `enabled` is false while an agent previews a portal: registering their
 * device would send this athlete's notifications to the wrong phone.
 */
export function usePushNotifications({ enabled = true }: { enabled?: boolean } = {}) {
  useEffect(() => {
    if (!enabled) return
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return
    if (Notification.permission !== 'granted') return
    if (!VAPID_PUBLIC_KEY) {
      console.error('[push] VITE_VAPID_PUBLIC_KEY is not set; push re-sync skipped.')
      return
    }

    let cancelled = false
    // Stay out of the way of first paint.
    const timer = setTimeout(async () => {
      try {
        const reg = await navigator.serviceWorker.ready
        let sub = await reg.pushManager.getSubscription()
        if (sub && !subscriptionKeyMatches(sub)) {
          await sub.unsubscribe()
          sub = null
        }
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationServerKey(),
          })
        }
        if (cancelled) return
        if (localStorage.getItem(PUSH_ENDPOINT_KEY) === sub.endpoint) return

        const session = getClientSession()
        if (!session?.token) return
        await saveSubscriptionToServer(sub, session.token)
      } catch (err) {
        console.error('[push] Re-sync failed:', err)
      }
    }, 10_000)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [enabled])
}
